(() => {
  if (window.wokieeSearchReturnPositionBound) return;

  const searchPath = new URL(window.routes?.search_url || '/search', window.location.origin).pathname;
  const collectionsPath = new URL(window.routes?.collections_url || '/collections', window.location.origin)
    .pathname.replace(/\/$/, '');
  const isSearchPage = window.location.pathname === searchPath;
  const isCollectionPage = window.location.pathname === collectionsPath ||
    window.location.pathname.startsWith(`${collectionsPath}/`);
  if (!isSearchPage && !isCollectionPage) return;

  window.wokieeSearchReturnPositionBound = true;

  const storageKey = 'wokieeListingReturnPositionV9';
  const gridSnapshotStorageKey = 'wokieeListingGridSnapshotV2';
  const historyStateKey = 'wokieeListingReturnV9';
  const legacyStorageKeys = [
    'wokieeListingReturnPositionV8',
    'wokieeListingReturnPositionV7',
    'wokieeListingReturnPositionV6',
    'wokieeListingReturnPositionV5',
    'wokieeSearchReturnPositionV4',
    'wokieeSearchReturnPositionV2',
  ];
  const productPath = '/products/';
  const restoreLifetime = 30 * 60 * 1000;
  const restoreDelays = [0, 50, 120, 250, 500, 900, 1500, 2500];
  let activeRestore = null;
  let restoreObserver = null;
  let restoreResizeObserver = null;
  let restoreTimers = [];
  let stabilityTimer = null;
  let previousScrollRestoration = null;
  let viewportSaveTimer = null;
  let rangeRestorePromise = null;
  let restoreSucceeded = false;
  let isApplyingScroll = false;
  let applyingScrollTimer = null;
  let scrollBehaviorCaptured = false;
  let previousInlineScrollBehavior = '';
  let previousInlineScrollBehaviorPriority = '';
  let latestGridSnapshot = null;
  let latestGridSnapshotStored = false;
  let snapshotRefreshTimer = null;
  let snapshotRefreshIdleCallback = null;
  let visibleCardObserver = null;
  let observedCards = new WeakSet();
  const visibleCards = new Map();
  let nearestVisibleCard = null;

  function getSearchKey(url) {
    const normalizedUrl = new URL(url, window.location.origin);
    normalizedUrl.hash = '';
    normalizedUrl.searchParams.delete('page');
    normalizedUrl.searchParams.sort();
    return `${normalizedUrl.pathname}?${normalizedUrl.searchParams.toString()}`;
  }

  function getEventElement(event) {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    return path.find((item) => item?.nodeType === 1) || event.target;
  }

  function readGridSnapshot(expectedSearchKey = '') {
    if (latestGridSnapshot &&
      (!expectedSearchKey || latestGridSnapshot.searchKey === expectedSearchKey)) {
      return latestGridSnapshot;
    }

    try {
      const rawSnapshot = window.sessionStorage.getItem(gridSnapshotStorageKey);
      if (!rawSnapshot) return null;
      const storedSnapshot = JSON.parse(rawSnapshot);
      if (!storedSnapshot ||
        (expectedSearchKey && storedSnapshot.searchKey !== expectedSearchKey)) return null;
      // Older versions cached the complete accumulated product-grid HTML here.
      // Keep only the small page-range metadata; missing pages are restored by
      // the existing batched fetch path when the customer comes back.
      delete storedSnapshot.gridHtml;
      latestGridSnapshot = storedSnapshot;
      latestGridSnapshotStored = true;
      window.sessionStorage.setItem(gridSnapshotStorageKey, JSON.stringify(storedSnapshot));
      return storedSnapshot;
    } catch (error) {
      return null;
    }
  }

  function addStoredGridSnapshot(savedPosition) {
    if (!savedPosition) return savedPosition;
    delete savedPosition.gridHtml;
    const gridSnapshot = readGridSnapshot(savedPosition.searchKey);
    return gridSnapshot ? { ...gridSnapshot, ...savedPosition } : savedPosition;
  }

  function readStoredPosition(includeGridSnapshot = false) {
    let savedPosition = null;
    try {
      const storedValue = window.sessionStorage.getItem(storageKey);
      if (storedValue) savedPosition = JSON.parse(storedValue);
    } catch (error) {
      // Browser history below is the storage-independent fallback.
    }
    if (!savedPosition) savedPosition = window.history.state?.[historyStateKey] || null;
    return includeGridSnapshot ? addStoredGridSnapshot(savedPosition) : savedPosition;
  }

  function writeStoredPosition(savedPosition) {
    try {
      const compactPosition = { ...savedPosition };
      delete compactPosition.gridHtml;
      legacyStorageKeys.forEach((key) => window.sessionStorage.removeItem(key));
      window.sessionStorage.setItem(storageKey, JSON.stringify(compactPosition));
    } catch (error) {
      // The URL anchor remains a storage-independent fallback.
    }
  }

  function removeStoredPosition() {
    try {
      window.sessionStorage.removeItem(storageKey);
      legacyStorageKeys.forEach((key) => window.sessionStorage.removeItem(key));
    } catch (error) {
      // Nothing else is required when storage is unavailable.
    }
    const currentState = window.history.state;
    if (currentState && typeof currentState === 'object' && currentState[historyStateKey]) {
      const cleanedState = { ...currentState };
      delete cleanedState[historyStateKey];
      window.history.replaceState(cleanedState, '', window.location.href);
    }
  }

  function clearRestoreWork() {
    restoreTimers.forEach((timer) => window.clearTimeout(timer));
    restoreTimers = [];
    if (stabilityTimer !== null) window.clearTimeout(stabilityTimer);
    stabilityTimer = null;
    restoreObserver?.disconnect();
    restoreObserver = null;
    restoreResizeObserver?.disconnect();
    restoreResizeObserver = null;
    if (applyingScrollTimer !== null) window.clearTimeout(applyingScrollTimer);
    applyingScrollTimer = null;
    isApplyingScroll = false;
  }

  function activateInstantScrollMode() {
    const html = document.documentElement;
    if (!html?.style) return;

    if (!scrollBehaviorCaptured) {
      previousInlineScrollBehavior = html.style.getPropertyValue('scroll-behavior');
      previousInlineScrollBehaviorPriority = html.style.getPropertyPriority('scroll-behavior');
      scrollBehaviorCaptured = true;
    }

    html.style.setProperty('scroll-behavior', 'auto', 'important');
  }

  function restoreInstantScrollMode() {
    if (!scrollBehaviorCaptured) return;

    const html = document.documentElement;
    if (html?.style) {
      if (previousInlineScrollBehavior) {
        html.style.setProperty(
          'scroll-behavior',
          previousInlineScrollBehavior,
          previousInlineScrollBehaviorPriority
        );
      } else {
        html.style.removeProperty('scroll-behavior');
      }
    }

    scrollBehaviorCaptured = false;
    previousInlineScrollBehavior = '';
    previousInlineScrollBehaviorPriority = '';
  }

  function restoreBrowserScrollMode() {
    if (previousScrollRestoration !== null && 'scrollRestoration' in window.history) {
      window.history.scrollRestoration = previousScrollRestoration;
      previousScrollRestoration = null;
    }
    delete window.wokieeListingPreviousScrollRestoration;
    restoreInstantScrollMode();
  }

  function revealListing() {
    document.documentElement?.classList?.remove('wokiee-listing-return-pending');
  }

  function removeUserIntentListeners() {
    ['wheel', 'touchstart', 'pointerdown', 'mousedown', 'keydown'].forEach((eventName) => {
      window.removeEventListener(eventName, cancelRestoreForUser);
    });
  }

  function completeRestore() {
    clearRestoreWork();
    removeStoredPosition();
    removeUserIntentListeners();
    restoreBrowserScrollMode();
    revealListing();
    activeRestore = null;
    restoreSucceeded = false;
    observeListingCards();
    scheduleListingSnapshotRefresh();
  }

  function cancelRestoreForUser() {
    if (!activeRestore) return;
    completeRestore();
  }

  function addUserIntentListeners() {
    ['wheel', 'touchstart', 'pointerdown', 'mousedown', 'keydown'].forEach((eventName) => {
      window.addEventListener(eventName, cancelRestoreForUser, { passive: true });
    });
  }

  function scheduleStableCompletion() {
    if (stabilityTimer !== null) window.clearTimeout(stabilityTimer);
    stabilityTimer = window.setTimeout(() => {
      if (rangeRestorePromise) {
        scheduleStableCompletion();
        return;
      }
      completeRestore();
    }, 700);
  }

  function applyProgrammaticScroll(targetScrollY) {
    isApplyingScroll = true;
    activateInstantScrollMode();
    window.scrollTo({ top: Math.max(0, targetScrollY), left: 0, behavior: 'auto' });
    if (applyingScrollTimer !== null) window.clearTimeout(applyingScrollTimer);
    applyingScrollTimer = window.setTimeout(() => {
      isApplyingScroll = false;
      applyingScrollTimer = null;
    }, 100);
  }

  function preserveThemeHistory(returnUrl, savedPosition = null) {
    let currentState = window.history.state && typeof window.history.state === 'object'
      ? { ...window.history.state }
      : window.history.state;

    if (currentState && typeof currentState === 'object') {
      delete currentState.wokieeSearchReturn;
      delete currentState.wokieeListingReturnV8;
    }

    if (savedPosition) {
      if (!currentState || typeof currentState !== 'object') currentState = {};
      const historyPosition = { ...savedPosition };
      delete historyPosition.gridHtml;
      currentState[historyStateKey] = historyPosition;
    }

    window.history.replaceState(currentState, '', returnUrl.toString());
  }

  function restoreOriginalListingUrl(savedPosition) {
    if (!savedPosition?.listingUrl) return;
    try {
      const listingUrl = new URL(savedPosition.listingUrl, window.location.origin);
      if (listingUrl.origin !== window.location.origin) return;
      listingUrl.hash = '';
      preserveThemeHistory(listingUrl);
    } catch (error) {
      // Keep the temporary target-page URL if the stored URL cannot be parsed.
    }
  }

  function isTerminalPaginationLink(link) {
    if (!link) return false;
    const href = (link.getAttribute('href') || '').trim();
    const button = link.closest('infinity-button');
    return !href || (
      button?.classList.contains('autoscroll_yes') && link.classList.contains?.('mt40')
    );
  }

  function normalizeTerminalPagination(link) {
    if (!isTerminalPaginationLink(link)) return false;
    link.setAttribute('aria-disabled', 'true');
    link.removeAttribute('href');
    link.classList.remove('loading', 'autoscroll', 'hide');
    link.classList.add('mt40');
    document.querySelector('.collection_facets_loader')?.classList.remove('loading_now');
    document.getElementById('ProductGridContainer')?.classList?.remove('loading_now');
    return true;
  }

  function getListingSnapshot() {
    const grid = document.getElementById('ProductGridContainer');
    if (!grid) return '';

    const paginationLinks = [...(grid.querySelectorAll?.('infinity-button a.btn') || [])];
    paginationLinks.forEach(normalizeTerminalPagination);
    const transientLinks = paginationLinks.filter((link) => !isTerminalPaginationLink(link));
    const linkStates = transientLinks.map((link) => ({
      ariaDisabled: link.getAttribute('aria-disabled'),
      className: link.getAttribute('class'),
      link,
    }));

    try {
      transientLinks.forEach((link) => {
        link.classList.remove('loading');
        link.removeAttribute('aria-disabled');
        if (link.closest('infinity-button')?.classList.contains('autoscroll_yes')) {
          link.classList.add('autoscroll', 'hide');
        }
      });
      return grid.innerHTML || '';
    } finally {
      linkStates.forEach(({ ariaDisabled, className, link }) => {
        if (className === null) link.removeAttribute('class');
        else link.setAttribute('class', className);
        if (ariaDisabled === null) link.removeAttribute('aria-disabled');
        else link.setAttribute('aria-disabled', ariaDisabled);
      });
    }
  }

  function getNextListingPageUrl() {
    const nextLink = document.querySelector('#ProductGridContainer infinity-button a[href]');
    if (!nextLink || isTerminalPaginationLink(nextLink)) {
      normalizeTerminalPagination(nextLink);
      return '';
    }
    const nextHref = nextLink.getAttribute('href') || '';
    if (!nextHref) return '';

    try {
      const nextUrl = new URL(nextHref, window.location.origin);
      const loadedPages = getLoadedPageNumbers();
      const highestLoadedPage = loadedPages.length ? Math.max(...loadedPages) : 0;
      const requestedPage = Number.parseInt(nextUrl.searchParams.get('page'), 10);
      if (highestLoadedPage && (!Number.isFinite(requestedPage) || requestedPage <= highestLoadedPage)) {
        nextUrl.searchParams.set('page', String(highestLoadedPage + 1));
      }
      return nextUrl.pathname + nextUrl.search;
    } catch (error) {
      return nextHref;
    }
  }

  function cancelSnapshotRefresh() {
    if (snapshotRefreshTimer !== null) window.clearTimeout(snapshotRefreshTimer);
    snapshotRefreshTimer = null;
    if (snapshotRefreshIdleCallback !== null && typeof window.cancelIdleCallback === 'function') {
      window.cancelIdleCallback(snapshotRefreshIdleCallback);
    }
    snapshotRefreshIdleCallback = null;
  }

  function persistGridSnapshot(gridSnapshot) {
    const compactSnapshot = { ...gridSnapshot };
    delete compactSnapshot.gridHtml;
    latestGridSnapshot = compactSnapshot;
    latestGridSnapshotStored = false;
    try {
      window.sessionStorage.setItem(gridSnapshotStorageKey, JSON.stringify(compactSnapshot));
      latestGridSnapshotStored = true;
    } catch (error) {
      try {
        window.sessionStorage.removeItem(gridSnapshotStorageKey);
      } catch (removeError) {
        // The in-memory snapshot remains available for BFCache navigation.
      }
    }
    return compactSnapshot;
  }

  function captureListingSnapshot() {
    const currentUrl = new URL(window.location.href);
    const page = Number.parseInt(currentUrl.searchParams.get('page'), 10) || 1;
    const loadedPages = getLoadedPageNumbers();
    const listingUrl = new URL(currentUrl);
    listingUrl.hash = '';
    listingUrl.searchParams.delete('page');
    return persistGridSnapshot({
      firstLoadedPage: loadedPages.length ? Math.min(...loadedPages) : page,
      lastLoadedPage: loadedPages.length ? Math.max(...loadedPages) : page,
      listingUrl: listingUrl.toString(),
      nextPageUrl: getNextListingPageUrl(),
      savedAt: Date.now(),
      searchKey: getSearchKey(currentUrl),
    });
  }

  function getCurrentGridSnapshot() {
    const currentSearchKey = getSearchKey(window.location.href);
    const gridSnapshot = readGridSnapshot(currentSearchKey);
    const loadedPages = getLoadedPageNumbers();
    const currentPage = Number.parseInt(new URL(window.location.href).searchParams.get('page'), 10) || 1;
    const firstLoadedPage = loadedPages.length ? Math.min(...loadedPages) : currentPage;
    const lastLoadedPage = loadedPages.length ? Math.max(...loadedPages) : currentPage;
    if (gridSnapshot &&
      Number.parseInt(gridSnapshot.firstLoadedPage, 10) === firstLoadedPage &&
      Number.parseInt(gridSnapshot.lastLoadedPage, 10) === lastLoadedPage) {
      return gridSnapshot;
    }
    return captureListingSnapshot();
  }

  function scheduleListingSnapshotRefresh() {
    if (activeRestore || snapshotRefreshTimer !== null || snapshotRefreshIdleCallback !== null) return;

    snapshotRefreshTimer = window.setTimeout(() => {
      snapshotRefreshTimer = null;
      const refreshSnapshot = () => {
        snapshotRefreshIdleCallback = null;
        if (!activeRestore) getCurrentGridSnapshot();
      };
      if (typeof window.requestIdleCallback === 'function') {
        snapshotRefreshIdleCallback = window.requestIdleCallback(refreshSnapshot, { timeout: 1200 });
      } else {
        refreshSnapshot();
      }
    }, 250);
  }

  function invalidateGridSnapshot() {
    cancelSnapshotRefresh();
    latestGridSnapshot = null;
    latestGridSnapshotStored = false;
    try {
      window.sessionStorage.removeItem(gridSnapshotStorageKey);
    } catch (error) {
      // A new in-memory snapshot can still be prepared when storage is blocked.
    }
  }

  function restorePaginationState(savedPosition) {
    const nextLink = document.querySelector('#ProductGridContainer infinity-button a[href]');
    if (!nextLink) return;
    if (isTerminalPaginationLink(nextLink)) {
      normalizeTerminalPagination(nextLink);
      return;
    }
    if (savedPosition?.nextPageUrl) nextLink.setAttribute('href', savedPosition.nextPageUrl);
    const repairedNextPageUrl = getNextListingPageUrl();
    if (repairedNextPageUrl) nextLink.setAttribute('href', repairedNextPageUrl);
    nextLink.classList.remove('loading');
    nextLink.removeAttribute('aria-disabled');
    if (nextLink.closest('infinity-button')?.classList.contains('autoscroll_yes')) {
      nextLink.classList.add('autoscroll', 'hide');
    }
  }

  function hasCompleteSavedRange(savedPosition) {
    const loadedPages = getLoadedPageNumbers();
    const targetPage = Number.parseInt(savedPosition?.page, 10) || 1;
    const desiredFirstPage = Number.parseInt(savedPosition?.firstLoadedPage, 10) || 1;
    const desiredLastPage = Number.parseInt(savedPosition?.lastLoadedPage, 10) || targetPage;
    return loadedPages.length &&
      Math.min(...loadedPages) <= desiredFirstPage &&
      Math.max(...loadedPages) >= desiredLastPage;
  }

  function restoreListingSnapshot(savedPosition) {
    if (!savedPosition?.gridHtml || hasCompleteSavedRange(savedPosition)) return false;
    const grid = document.getElementById('ProductGridContainer');
    if (!grid) return false;

    grid.innerHTML = savedPosition.gridHtml;
    restorePaginationState(savedPosition);
    window.dispatchEvent(new Event('createProductView'));
    restoreOriginalListingUrl(savedPosition);
    return true;
  }

  function getCardProductPathname(card) {
    const link = card?.querySelector?.('a[href]');
    if (!link) return '';
    try {
      const productUrl = new URL(link.href, window.location.origin);
      return productUrl.origin === window.location.origin && productUrl.pathname.startsWith(productPath)
        ? productUrl.pathname
        : '';
    } catch (error) {
      return '';
    }
  }

  function chooseNearestVisibleCard(cards) {
    const viewportCenter = window.innerHeight / 2;
    let closestCard = null;
    let closestDistance = Number.POSITIVE_INFINITY;
    cards.forEach((card) => {
      if (card?.isConnected === false) {
        visibleCards.delete(card);
        return;
      }
      const rect = card?.getBoundingClientRect?.();
      if (!rect || rect.bottom <= 0 || rect.top >= window.innerHeight) return;
      const distance = Math.abs(rect.top + (rect.height / 2) - viewportCenter);
      if (distance < closestDistance) {
        closestCard = card;
        closestDistance = distance;
      }
    });
    return closestCard;
  }

  function refreshNearestVisibleCard() {
    nearestVisibleCard = chooseNearestVisibleCard([...visibleCards.keys()]);
    return nearestVisibleCard;
  }

  function observeListingCards() {
    if (typeof IntersectionObserver === 'undefined') return;
    if (!visibleCardObserver) {
      visibleCardObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) visibleCards.set(entry.target, entry.intersectionRatio);
          else visibleCards.delete(entry.target);
        });
        refreshNearestVisibleCard();
      }, { threshold: [0, 0.25, 0.5, 0.75, 1] });
    }

    document.querySelectorAll('#ProductGridContainer .product-card').forEach((card) => {
      if (observedCards.has(card)) return;
      observedCards.add(card);
      visibleCardObserver.observe(card);
    });
  }

  function getNearestVisibleCard() {
    if (visibleCards.size) {
      const observedCard = refreshNearestVisibleCard();
      if (observedCard) return observedCard;
    }

    const elementAtPoint = document.elementFromPoint?.(
      Math.max(1, Math.round((window.innerWidth || 1) / 2)),
      Math.max(1, Math.round(window.innerHeight / 2))
    );
    const centeredCard = elementAtPoint?.closest?.('#ProductGridContainer .product-card');
    if (centeredCard) return centeredCard;

    // Compatibility fallback for old browsers and the deterministic test DOM.
    // Modern storefronts use IntersectionObserver and never scan the full grid
    // on every scroll tick.
    return chooseNearestVisibleCard([
      ...document.querySelectorAll('#ProductGridContainer .product-card'),
    ]);
  }

  function saveViewportPosition(includeSnapshot = false) {
    viewportSaveTimer = null;
    if (activeRestore) return;

    const existingPosition = readStoredPosition();
    if (existingPosition?.pendingProductReturn) return;

    const card = getNearestVisibleCard();
    const currentUrl = new URL(window.location.href);
    const page = Number.parseInt(card?.dataset.searchResultPage, 10) ||
      Number.parseInt(currentUrl.searchParams.get('page'), 10) || 1;
    const savedPosition = {
      cardId: card?.id || '',
      cardTop: card ? card.getBoundingClientRect().top : null,
      page,
      pendingProductReturn: false,
      productPathname: getCardProductPathname(card),
      searchKey: getSearchKey(currentUrl),
      savedAt: Date.now(),
      scrollY: window.scrollY || window.pageYOffset || 0,
    };

    if (includeSnapshot) {
      const gridSnapshot = getCurrentGridSnapshot();
      savedPosition.firstLoadedPage = gridSnapshot.firstLoadedPage;
      savedPosition.lastLoadedPage = gridSnapshot.lastLoadedPage;
      savedPosition.listingUrl = gridSnapshot.listingUrl;
      savedPosition.nextPageUrl = gridSnapshot.nextPageUrl;
      if (!latestGridSnapshotStored) savedPosition.gridHtml = gridSnapshot.gridHtml;
    }

    writeStoredPosition(savedPosition);
  }

  function queueViewportPositionSave() {
    if (activeRestore || viewportSaveTimer !== null) return;
    viewportSaveTimer = window.setTimeout(saveViewportPosition, 250);
  }

  function rememberSearchResult(event) {
    if (typeof event.button === 'number' && event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const eventElement = getEventElement(event);
    const link = eventElement?.closest?.('a[href]');
    const card = link?.closest?.('.product-card');
    if (!link || !card?.id) return;

    let productUrl;
    try {
      productUrl = new URL(link.href, window.location.origin);
    } catch (error) {
      return;
    }

    if (productUrl.origin !== window.location.origin || !productUrl.pathname.startsWith(productPath)) return;

    clearRestoreWork();
    removeUserIntentListeners();
    restoreBrowserScrollMode();
    activeRestore = null;
    restoreSucceeded = false;
    cancelSnapshotRefresh();

    const returnUrl = new URL(window.location.href);
    returnUrl.hash = '';
    const page = Number.parseInt(card.dataset.searchResultPage, 10) ||
      Number.parseInt(returnUrl.searchParams.get('page'), 10) || 1;
    const gridSnapshot = getCurrentGridSnapshot();
    const virtualListingState = window.wokieeListingWindow?.getRestoreState?.();
    const savedPosition = {
      cardId: card.id,
      cardTop: card.getBoundingClientRect().top,
      firstLoadedPage: gridSnapshot.firstLoadedPage,
      lastLoadedPage: gridSnapshot.lastLoadedPage,
      listingUrl: gridSnapshot.listingUrl,
      nextPageUrl: gridSnapshot.nextPageUrl,
      page,
      pendingProductReturn: true,
      productPathname: productUrl.pathname,
      searchKey: getSearchKey(returnUrl),
      savedAt: Date.now(),
      scrollY: window.scrollY || window.pageYOffset || 0,
      virtualListingState,
    };
    if (!latestGridSnapshotStored) savedPosition.gridHtml = gridSnapshot.gridHtml;

    writeStoredPosition(savedPosition);

    if (page > 1) {
      returnUrl.searchParams.set('page', String(page));
    } else {
      returnUrl.searchParams.delete('page');
    }

    // The anchor lets the browser restore the exact card even if sessionStorage
    // is blocked. JavaScript below corrects any later Wokiee/image layout shift.
    returnUrl.hash = encodeURIComponent(card.id);
    preserveThemeHistory(returnUrl, savedPosition);
  }

  function getHashCardId() {
    if (!window.location.hash) return '';
    try {
      const cardId = decodeURIComponent(window.location.hash.slice(1));
      return cardId.startsWith('product-') ? cardId : '';
    } catch (error) {
      return '';
    }
  }

  function findSavedCard(savedPosition) {
    const cardId = savedPosition?.cardId || getHashCardId();
    const cardById = cardId
      ? document.getElementById(cardId) || window.wokieeListingWindow?.materializeCard?.(cardId)
      : null;
    if (cardById) return cardById;

    if (!savedPosition?.productPathname) return null;
    return [...document.querySelectorAll('.product-card a[href]')]
      .find((link) => {
        try {
          return new URL(link.href, window.location.origin).pathname === savedPosition.productPathname;
        } catch (error) {
          return false;
        }
      })
      ?.closest('.product-card') || null;
  }

  function getLoadedPageNumbers() {
    const container = document.querySelector('#ProductGridContainer .collection__container');
    const virtualPages = window.wokieeListingWindow?.getLoadedPages?.(container);
    if (virtualPages?.size) return [...virtualPages].sort((firstPage, secondPage) => firstPage - secondPage);
    return [...new Set([...document.querySelectorAll(
      '#ProductGridContainer .product-card[data-search-result-page], ' +
      '#ProductGridContainer wokiee-virtual-card[data-search-result-page], ' +
      '#ProductGridContainer wokiee-virtual-page[data-wokiee-page]'
    )]
      .map((card) => Number.parseInt(
        card.dataset.searchResultPage || card.dataset.wokieePage,
        10
      ))
      .filter(Number.isFinite))].sort((firstPage, secondPage) => firstPage - secondPage);
  }

  async function fetchListingPage(baseUrl, page) {
    const pageUrl = new URL(baseUrl, window.location.origin);
    pageUrl.hash = '';
    pageUrl.searchParams.set('page', String(page));
    const requestUrl = pageUrl.pathname + pageUrl.search;
    const cachedHtml = await window.wokieeListingPageCache?.match?.(requestUrl);
    if (cachedHtml) {
      const cachedPage = new DOMParser().parseFromString(cachedHtml, 'text/html');
      if (cachedPage.querySelector('#ProductGridContainer .collection__container')) return cachedPage;
    }

    const response = await fetch(requestUrl, { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`Return position page ${page} failed: ${response.status}`);
    const responseHtml = await response.text();
    window.wokieeListingPageCache?.put?.(requestUrl, responseHtml);
    return new DOMParser().parseFromString(responseHtml, 'text/html');
  }

  async function restoreListingRange(savedPosition) {
    const targetPage = Number.parseInt(savedPosition?.page, 10) || 1;
    const desiredFirstPage = Number.parseInt(savedPosition?.firstLoadedPage, 10) || 1;
    const desiredLastPage = Number.parseInt(savedPosition?.lastLoadedPage, 10) || targetPage;
    const loadedPages = getLoadedPageNumbers();
    const loadedFirstPage = loadedPages.length ? Math.min(...loadedPages) : targetPage;
    const loadedLastPage = loadedPages.length ? Math.max(...loadedPages) : targetPage;
    const missingBefore = [];
    const missingAfter = [];
    for (let page = desiredFirstPage; page < loadedFirstPage; page += 1) missingBefore.push(page);
    for (let page = loadedLastPage + 1; page <= desiredLastPage; page += 1) missingAfter.push(page);
    const missingPages = [...missingBefore, ...missingAfter];
    if (!missingPages.length) return;

    const currentContainer = document.querySelector('#ProductGridContainer .collection__container');
    if (!currentContainer) return;

    document.querySelector('#ProductGridContainer infinity-button a.autoscroll')
      ?.classList.remove('autoscroll');

    const parsedByPage = new Map();
    for (let index = 0; index < missingPages.length; index += 6) {
      const batchPages = missingPages.slice(index, index + 6);
      const parsedPages = await Promise.allSettled(
        batchPages.map((page) => fetchListingPage(savedPosition.listingUrl || window.location.href, page))
      );
      const failedPages = [];
      parsedPages.forEach((result, pageIndex) => {
        if (result.status === 'fulfilled') parsedByPage.set(batchPages[pageIndex], result.value);
        else failedPages.push(batchPages[pageIndex]);
      });

      if (failedPages.length) {
        const retriedPages = await Promise.allSettled(
          failedPages.map((page) => fetchListingPage(savedPosition.listingUrl || window.location.href, page))
        );
        retriedPages.forEach((result, pageIndex) => {
          if (result.status === 'fulfilled') parsedByPage.set(failedPages[pageIndex], result.value);
          else console.error(result.reason);
        });
      }
    }

    const viewportAnchor = getNearestVisibleCard() || findSavedCard(savedPosition);
    const viewportAnchorId = viewportAnchor?.id || '';
    const anchorTopBeforeInsert = viewportAnchor?.getBoundingClientRect().top ?? null;
    const getPageMarkup = (page) => {
      const pageContainer = parsedByPage.get(page)
        ?.querySelector('#ProductGridContainer .collection__container');
      return pageContainer?.innerHTML || '';
    };
    const availableBefore = missingBefore.filter((page) => parsedByPage.has(page));
    const availableAfter = missingAfter.filter((page) => parsedByPage.has(page));

    const scrollingElement = document.scrollingElement || document.documentElement;
    const previousOverflowAnchor = scrollingElement?.style?.overflowAnchor;
    if (scrollingElement?.style) scrollingElement.style.overflowAnchor = 'none';

    try {
      const insertSerializedPages = window.wokieeListingWindow?.insertSerializedPages;
      if (availableBefore.length) {
        const pagesBefore = availableBefore.map((page) => ({ page, html: getPageMarkup(page) }));
        if (!insertSerializedPages?.(currentContainer, pagesBefore, 'before')) {
          currentContainer.insertAdjacentHTML('afterbegin', pagesBefore.map((item) => item.html).join(''));
        }
      }
      if (availableAfter.length) {
        const pagesAfter = availableAfter.map((page) => ({ page, html: getPageMarkup(page) }));
        if (!insertSerializedPages?.(currentContainer, pagesAfter, 'after')) {
          currentContainer.insertAdjacentHTML('beforeend', pagesAfter.map((item) => item.html).join(''));
        }
      }

      window.dispatchEvent(new Event('createProductView'));

      if (viewportAnchorId && anchorTopBeforeInsert !== null) {
        const anchorAfterInsert = document.getElementById(viewportAnchorId);
        if (anchorAfterInsert) {
          const layoutShift = anchorAfterInsert.getBoundingClientRect().top - anchorTopBeforeInsert;
          if (Math.abs(layoutShift) > 0.5) {
            applyProgrammaticScroll((window.scrollY || window.pageYOffset || 0) + layoutShift);
          }
        }
      }
    } finally {
      if (scrollingElement?.style) scrollingElement.style.overflowAnchor = previousOverflowAnchor || '';
    }

    const lastPageDocument = parsedByPage.get(desiredLastPage);
    if (lastPageDocument) {
      const currentButton = document.querySelector('#ProductGridContainer infinity-button');
      const nextButton = lastPageDocument.querySelector('#ProductGridContainer infinity-button');
      if (currentButton && nextButton) {
        currentButton.replaceWith(nextButton);
      } else if (currentButton) {
        currentButton.remove();
      }
    }

    restoreOriginalListingUrl(savedPosition);
  }

  function ensureListingRangeIsRestored() {
    if (!activeRestore || rangeRestorePromise) return;
    const savedPosition = activeRestore;
    if (hasCompleteSavedRange(savedPosition)) {
      restoreOriginalListingUrl(savedPosition);
      return;
    }

    rangeRestorePromise = restoreListingRange(savedPosition)
      .then(restoreSavedCard)
      .catch((error) => console.error(error))
      .finally(() => {
        rangeRestorePromise = null;
      });
  }

  function restoreSavedCard() {
    if (!activeRestore) return false;

    const card = findSavedCard(activeRestore);
    if (!card) {
      ensureListingRangeIsRestored();
      const currentPage = Number.parseInt(new URL(window.location.href).searchParams.get('page'), 10) || 1;
      if (currentPage === activeRestore.page && Number.isFinite(activeRestore.scrollY)) {
        window.scrollTo({ top: Math.max(0, activeRestore.scrollY), left: 0, behavior: 'auto' });
      }
      return false;
    }

    const isFirstSuccessfulRestore = !restoreSucceeded;

    const cardTop = card.getBoundingClientRect().top;
    const savedCardTop = Number.isFinite(activeRestore.cardTop) ? activeRestore.cardTop : null;
    const targetScrollY = savedCardTop === null
      ? (window.scrollY || window.pageYOffset || 0) + cardTop - (window.innerHeight / 3)
      : (window.scrollY || window.pageYOffset || 0) + cardTop - savedCardTop;

    const currentScrollY = window.scrollY || window.pageYOffset || 0;
    if (Math.abs(targetScrollY - currentScrollY) > 0.5) applyProgrammaticScroll(targetScrollY);
    card.setAttribute('data-search-return-target', 'true');
    restoreSucceeded = true;
    if (isFirstSuccessfulRestore) {
      restoreTimers.forEach((timer) => window.clearTimeout(timer));
      restoreTimers = [];
    }
    scheduleStableCompletion();
    ensureListingRangeIsRestored();
    return true;
  }

  function finishRestore() {
    if (rangeRestorePromise && !restoreSucceeded) {
      restoreTimers.push(window.setTimeout(finishRestore, 500));
      return;
    }
    if (!restoreSucceeded) restoreSavedCard();
    completeRestore();
  }

  function isBackNavigation(event) {
    if (event?.persisted) return true;
    try {
      return window.performance?.getEntriesByType?.('navigation')?.[0]?.type === 'back_forward';
    } catch (error) {
      return false;
    }
  }

  function startRestore(event) {
    if (activeRestore) {
      restoreSavedCard();
      return;
    }

    let storedPosition = readStoredPosition();
    const hashCardId = getHashCardId();
    if (!storedPosition && !hashCardId) {
      revealListing();
      return;
    }

    const isHistoryReturn = isBackNavigation(event);
    const shouldRestoreListing = isHistoryReturn || !!hashCardId;
    if (!shouldRestoreListing) {
      if (storedPosition?.pendingProductReturn) removeStoredPosition();
      revealListing();
      return;
    }

    storedPosition = addStoredGridSnapshot(storedPosition);

    if (storedPosition) {
      const isFresh = Number.isFinite(storedPosition.savedAt) &&
        Date.now() - storedPosition.savedAt <= restoreLifetime;
      const isSameSearch = storedPosition.searchKey === getSearchKey(window.location.href);
      if (!isFresh || !isSameSearch) {
        removeStoredPosition();
        revealListing();
        return;
      }
      activeRestore = storedPosition;
    } else {
      activeRestore = {
        cardId: hashCardId,
        page: Number.parseInt(new URL(window.location.href).searchParams.get('page'), 10) || 1,
        savedAt: Date.now(),
      };
    }

    if ('scrollRestoration' in window.history) {
      previousScrollRestoration = window.wokieeListingPreviousScrollRestoration ||
        window.history.scrollRestoration;
      window.history.scrollRestoration = 'manual';
    }
    activateInstantScrollMode();
    window.wokieeListingWindow?.setRestoreState?.(activeRestore.virtualListingState);

    addUserIntentListeners();

    restoreListingSnapshot(activeRestore);
    const restoredImmediately = restoreSavedCard();

    // The server-rendered target page is usable immediately. Missing earlier
    // pages are restored behind it without holding the whole storefront hidden.
    revealListing();
    rangeRestorePromise?.finally(restoreSavedCard);

    // Do not schedule corrections after a successful restore: those delayed
    // callbacks used to pull the visitor back for up to eight seconds.
    if (!restoredImmediately) {
      restoreTimers = restoreDelays.map((delay) => window.setTimeout(restoreSavedCard, delay));
      restoreTimers.push(window.setTimeout(finishRestore, restoreDelays[restoreDelays.length - 1] + 250));
    }

    const grid = document.getElementById('ProductGridContainer');
    if (grid && typeof MutationObserver !== 'undefined') {
      restoreObserver = new MutationObserver(restoreSavedCard);
      restoreObserver.observe(grid, { childList: true, subtree: true });
    }
    if (grid && typeof ResizeObserver !== 'undefined') {
      restoreResizeObserver = new ResizeObserver(restoreSavedCard);
      restoreResizeObserver.observe(grid);
    }
  }

  function handleListingScroll() {
    if (activeRestore && restoreSucceeded && !isApplyingScroll) {
      cancelRestoreForUser();
    }
    queueViewportPositionSave();
  }

  function handleListingPageHide(event) {
    // Do not serialize the whole product grid while an ordinary navigation is
    // already waiting. Product clicks and the idle task prepare it beforehand.
    saveViewportPosition(false);

    const savedPosition = readStoredPosition();
    if (!savedPosition?.pendingProductReturn) {
      // Preserve the old hard-navigation fallback only when BFCache is not
      // available. Normally this reuses the already prepared snapshot and does
      // not touch live grid HTML during navigation.
      if (!event?.persisted) getCurrentGridSnapshot();
      return;
    }

    activateInstantScrollMode();
    document.documentElement?.classList?.add('wokiee-listing-return-pending');
    if ('scrollRestoration' in window.history) {
      if (!window.wokieeListingPreviousScrollRestoration) {
        window.wokieeListingPreviousScrollRestoration = window.history.scrollRestoration;
      }
      window.history.scrollRestoration = 'manual';
    }
  }

  function handleListingGridUpdate() {
    restoreSavedCard();
    observeListingCards();
    if (activeRestore) return;
    invalidateGridSnapshot();
    scheduleListingSnapshotRefresh();
  }

  function handleProductViewCreated() {
    restoreSavedCard();
    observeListingCards();
    if (activeRestore) return;
    invalidateGridSnapshot();
    scheduleListingSnapshotRefresh();
  }

  function handleListingLoad() {
    restoreSavedCard();
    observeListingCards();
    scheduleListingSnapshotRefresh();
  }

  // Window capture runs before Wokiee's document/card click handlers, even if
  // one of them stops immediate propagation or performs its own navigation.
  window.addEventListener('click', rememberSearchResult, true);

  window.addEventListener('scroll', handleListingScroll, { passive: true });
  window.addEventListener('pagehide', handleListingPageHide);
  window.addEventListener('pageshow', startRestore);
  window.addEventListener('load', handleListingLoad);
  window.addEventListener('updateGrid', handleListingGridUpdate);
  window.addEventListener('createProductView', handleProductViewCreated);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startRestore, { once: true });
  } else {
    startRestore();
  }
  observeListingCards();
  scheduleListingSnapshotRefresh();
})();
